import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, AlertTriangle, Lock, UserCog, BookOpen, CheckCircle2, ImagePlus, ChevronLeft, ChevronRight, Star, Save, Send } from "lucide-react";
import { toast } from "sonner";
import { api, media } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { fmtPrice, vTitle, fmtNum, fmtDate, OFFICIAL_PHONE } from "@/lib/format";
import { PageTitle, Loader, Empty, Section, Field, RestrictionToggles, StatusBadge } from "@/components/xd/ui";
import { AssistantSection } from "@/components/xd/AssistantSection";
import { SpecsForm, EMPTY_SPECS, specsPayload, pickSpecs, PhotoUploader } from "@/components/xd/VehicleForm";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const REVIEW_CLS = { approved: "text-emerald-400", pending: "text-amber-300", in_review: "text-sky-300", changes_pending: "text-amber-300", changes_requested: "text-orange-300", rejected: "text-red-400", changes_rejected: "text-red-400", draft: "text-zinc-400" };
export const ReviewTag = ({ s }) => {
  const { t } = useLang();
  return <span className={`text-xs ${REVIEW_CLS[s] || ""}`} data-testid="review-status">{t(`review.${s}`)}</span>;
};

export const AvailabilitySelect = ({ value, onChange, testid }) => {
  const { t } = useLang();
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="xd-input w-36" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{["available", "reserved", "sold"].map((s) => <SelectItem key={s} value={s}>{t(`status.${s}`)}</SelectItem>)}</SelectContent>
    </Select>
  );
};

const wasApproved = (v) => !!v.published_at || ["approved", "changes_pending", "changes_rejected"].includes(v.review_status);
const TAB_FILTER = {
  approved: wasApproved,
  pending: (v) => (!wasApproved(v) && ["pending", "in_review", "changes_requested", "rejected"].includes(v.review_status)) || (wasApproved(v) && ["changes_pending", "changes_requested"].includes(v.review_status)),
  drafts: (v) => v.review_status === "draft",
};
const TAB_TITLE = { approved: "dealer.vehicles", pending: "dealer.tab_pending", drafts: "dealer.tab_drafts" };

const DealerRow = ({ v, tab, onAvail, onDelete }) => {
  const { t, lang } = useLang();
  const cover = v.photos[0];
  return (
    <div className="xd-panel p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`dealer-vehicle-row-${v.code}`}>
      <Link to={`/dealer/v/${v.id}`} className="flex items-center gap-4 flex-1 min-w-0">
        <div className="relative w-28 h-20 shrink-0 bg-[#111] overflow-hidden">
          {cover && <img src={media(cover.id, "thumb")} alt="" className="h-full w-full object-cover" />}
          {tab === "approved" && <StatusBadge status={v.availability} className="absolute top-1 start-1 !text-[10px] !px-1.5 !py-0.5" />}
        </div>
        <div className="min-w-0 space-y-0.5">
          <p className="font-medium line-clamp-2 break-words">{[v.brand, v.model, v.year].filter(Boolean).join(" ") || t("dealer.untitled")}</p>
          <p className="text-xs text-zinc-500 ltr-num">{v.code}</p>
          <p className="text-xs text-zinc-500"><ReviewTag s={v.review_status} />
            {tab === "approved" && <> · {v.published ? t("dealer.live") : t("dealer.not_live")} · {t("dealer.public_price")}: <span className="ltr-num">{fmtPrice(v.advertised_price)}</span></>}
            {tab === "pending" && v.submitted_at && <> · {t("dealer.submitted_on")} <span className="ltr-num">{fmtDate(v.submitted_at)}</span></>}
            {tab === "drafts" && <> · {t("dealer.updated_on")} <span className="ltr-num">{fmtDate(v.updated_at)}</span></>}</p>
          {v.rejection_reason && <p className="text-xs text-orange-300 mt-1 line-clamp-3" data-testid={`dealer-feedback-${v.code}`}>{t("dealer.admin_feedback")}: {v.rejection_reason}</p>}
        </div>
      </Link>
      {tab === "approved" && <AvailabilitySelect value={v.availability} onChange={(a) => onAvail(v, a)} testid={`dealer-availability-${v.code}`} />}
      {tab === "pending" && ["changes_requested", "rejected"].includes(v.review_status) && <Link to={`/dealer/v/${v.id}`} className="btn-xd !min-h-[2.5rem] text-sm justify-center" data-testid={`dealer-resubmit-${v.code}`}>{t("dealer.fix_resubmit")}</Link>}
      {tab === "drafts" && (
        <div className="flex gap-2">
          <Link to={`/dealer/v/${v.id}`} className="btn-xd !min-h-[2.5rem] text-sm flex-1 justify-center" data-testid={`dealer-continue-${v.code}`}>{t("dealer.continue_draft")}</Link>
          {!v.submitted_at && <button type="button" onClick={() => onDelete(v)} className="btn-ghost !min-h-[2.5rem]" aria-label={t("dealer.delete_draft")} data-testid={`dealer-delete-draft-${v.code}`}><Trash2 size={15} /></button>}
        </div>
      )}
    </div>
  );
};

export function DealerVehicles({ tab = "approved" }) {
  const { t, tErr } = useLang();
  const [items, setItems] = useState(null);
  const load = () => api.get("/dealer/vehicles").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  const setAvail = async (v, a) => {
    try { await api.put(`/dealer/vehicles/${v.id}/availability`, { availability: a }); toast.success(t("common.saved")); load(); } catch (e) { toast.error(tErr(e)); }
  };
  const del = async (v) => {
    if (!window.confirm(t("dealer.delete_draft_confirm"))) return;
    try { await api.delete(`/dealer/vehicles/${v.id}`); load(); } catch (e) { toast.error(tErr(e)); }
  };
  const list = items && items.filter(TAB_FILTER[tab]);
  return (
    <div data-testid={`dealer-tab-${tab}`}>
      <PageTitle eyebrow={t("dealer.portal")} title={t(TAB_TITLE[tab])}>
        <Link to="/dealer/new" className="btn-xd" data-testid="dealer-new-vehicle-button"><Plus size={16} />{t("dealer.new")}</Link>
      </PageTitle>
      <p className="text-sm text-zinc-500 mb-4">{t(`dealer.tab_${tab}_hint`)}</p>
      {!list ? <Loader /> : !list.length ? <Empty text={t(`dealer.empty_${tab}`)} /> : (
        <div className="space-y-3">{list.map((v) => <DealerRow key={v.id} v={v} tab={tab} onAvail={setAvail} onDelete={del} />)}</div>
      )}
    </div>
  );
}

const EMPTY_CONF = { asking_price: "", final_price: "", contact_name: "", contact_phone: "" };
const RESTR = { facebook: true, instagram: true, tiktok: true, telegram: true, yad2: true };

const PriceInput = ({ letter, title, hint, value, onChange, tone, testid }) => (
  <div className={`xd-panel p-4 space-y-2 border-t-2 ${tone} min-w-0`}>
    <p className="text-sm"><span className="ltr-num font-semibold inline-block mx-1.5">{letter}</span>{title}</p>
    <Input required type="number" min="1" dir="ltr" aria-label={title} className="xd-input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    <p className="text-[11px] text-zinc-500 leading-relaxed">{hint}</p>
  </div>
);

const DealerPricing = ({ conf, setConf, publicPrice, bChanged, isNew, priceOk, setPriceOk }) => {
  const { t } = useLang();
  const bad = conf.asking_price && conf.final_price && Number(conf.final_price) > Number(conf.asking_price);
  return (
    <Section title={t("price.dealer_title")} testid="dealer-pricing-section">
      <p className="text-sm text-zinc-400 flex items-start gap-2"><Lock size={15} className="mt-0.5 shrink-0" />{t("price.dealer_intro")}</p>
      <div className="grid md:grid-cols-3 gap-3">
        <PriceInput letter="A" title={t("price.a_title")} hint={t("price.a_dealer_hint")} value={conf.asking_price} onChange={(x) => setConf({ ...conf, asking_price: x })} tone="border-t-zinc-500" testid="conf-asking_price-input" />
        <PriceInput letter="B" title={t("price.b_title")} hint={t("price.b_dealer_hint")} value={conf.final_price} onChange={(x) => setConf({ ...conf, final_price: x })} tone="border-t-amber-400" testid="conf-final_price-input" />
        <div className="xd-panel p-4 space-y-2 border-t-2 border-t-[var(--xd-red)] min-w-0" data-testid="dealer-price-c">
          <p className="text-sm"><span className="ltr-num font-semibold inline-block mx-1.5">C</span>{t("price.c_title")}</p>
          <p className="ltr-num text-xl h-11 flex items-center">{fmtPrice(publicPrice)}</p>
          <p className="text-[11px] text-zinc-500 leading-relaxed">{t("price.c_dealer_hint")}</p>
        </div>
      </div>
      {bad && <p className="text-sm text-red-400" data-testid="price-b-above-a">{t("err.final_above_asking")}</p>}
      {conf.final_price && <p className="text-xs text-zinc-500">B: <span className="ltr-num text-amber-300">₪{fmtNum(conf.final_price)}</span></p>}
      {bChanged && !isNew && <p className="text-sm text-amber-300 border border-amber-500/40 bg-amber-950/20 p-3" data-testid="price-b-change-note">{t("price.b_change_note")}</p>}
      {(bChanged || isNew) && (
        <label className="flex items-start gap-3 text-sm border border-white/10 p-3">
          <Checkbox checked={priceOk} onCheckedChange={(c) => setPriceOk(!!c)} className="mt-0.5" data-testid="price-b-confirm-checkbox" />{t("price.b_confirm")}
        </label>
      )}
    </Section>
  );
};

const DealerContact = ({ conf, setConf, override, setOverride, profile }) => {
  const { t } = useLang();
  return (
    <Section title={t("profile.vehicle_contact")} testid="dealer-contact-section">
      <p className="text-xs text-zinc-500">{t("dealer.confidential_note")}</p>
      <div className="xd-panel p-4 text-sm flex flex-wrap items-center justify-between gap-3" data-testid="dealer-profile-contact">
        <span>{profile?.contact_person} · <span className="ltr-num">{profile?.contact_phone}</span></span>
        <Link to="/dealer/profile" className="text-xs text-zinc-400 underline hover:text-white" data-testid="dealer-edit-profile-link">{t("profile.edit")}</Link>
      </div>
      <label className="flex items-center gap-3 text-sm"><Checkbox checked={override} onCheckedChange={(c) => setOverride(!!c)} data-testid="contact-override-checkbox" />{t("profile.use_other")}</label>
      {override && (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t("conf.contact_name")}><Input required className="xd-input" value={conf.contact_name} onChange={(e) => setConf({ ...conf, contact_name: e.target.value })} data-testid="conf-contact_name-input" /></Field>
          <Field label={t("conf.contact_phone")}><Input required type="tel" dir="ltr" className="xd-input" value={conf.contact_phone} onChange={(e) => setConf({ ...conf, contact_phone: e.target.value })} data-testid="conf-contact_phone-input" /></Field>
        </div>
      )}
    </Section>
  );
};

const PhotoRules = ({ checked, onChange }) => {
  const { t } = useLang();
  return (
    <div className="border border-[var(--xd-red)]/50 bg-red-950/10 p-5 space-y-3" data-testid="photo-rules">
      <p className="text-sm font-medium flex items-center gap-2"><AlertTriangle size={16} className="text-[var(--xd-red)]" />{t("photos.rules_title")}</p>
      <ul className="text-sm text-zinc-300 list-disc ps-5 space-y-1">{[1, 2, 3, 4].map((n) => <li key={n}>{t(`photos.rule${n}`)}</li>)}</ul>
      <p className="text-sm text-emerald-300">{t("photos.rule_plates")}</p>
      <p className="text-xs text-zinc-500">{t("photos.rule_originals")}</p>
      <label className="flex items-center gap-3 text-sm pt-1">
        <Checkbox checked={checked} onCheckedChange={(c) => onChange(!!c)} data-testid="photo-rules-checkbox" />{t("photos.rules_confirm")}
      </label>
    </div>
  );
};

const DealerPhotos = ({ v, ensureId, onChange }) => {
  const { t, tErr } = useLang();
  const [busy, setBusy] = useState(false);
  const [previews, setPreviews] = useState([]);
  const photos = v.photos || [];
  const canOrder = !v.published;
  const upload = async (files) => {
    setBusy(true);
    setPreviews(files.map((f) => URL.createObjectURL(f)));
    try {
      const vid = await ensureId();
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/dealer/vehicles/${vid}/photos`, fd);
      data.errors.forEach((e) => toast.error(`${e.file}: ${t(`err.${e.error}`)}`));
      onChange(data.vehicle);
    } catch (e) { toast.error(tErr(e)); } finally { setPreviews((p) => { p.forEach((u) => URL.revokeObjectURL(u)); return []; }); setBusy(false); }
  };
  const remove = async (pid) => { if (!window.confirm(`${t("common.delete")}?`)) return; try { onChange((await api.delete(`/dealer/vehicles/${v.id}/photos/${pid}`)).data); } catch (e) { toast.error(tErr(e)); } };
  const move = async (i, to) => {
    const ids = photos.map((p) => p.id);
    const [x] = ids.splice(i, 1);
    ids.splice(to, 0, x);
    try { onChange((await api.put(`/dealer/vehicles/${v.id}/photo-order`, { ids })).data); } catch (e) { toast.error(tErr(e)); }
  };
  const tile = "h-8 w-8 grid place-items-center text-zinc-400 hover:text-white disabled:opacity-30";
  return (
    <Section title={t("photos.vehicle_title", { n: photos.length })} actions={<PhotoUploader onFiles={upload} busy={busy} testid="dealer-photo-upload-input" />} testid="dealer-photos-section">
      <p className="text-xs text-zinc-500">{t("photos.first_hint")}</p>
      {!photos.length && !previews.length ? (
        <label className="border border-dashed border-white/20 min-h-[9rem] grid place-items-center text-center text-sm text-zinc-400 cursor-pointer hover:border-white/40 px-4" data-testid="dealer-photo-dropzone">
          <input type="file" multiple accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif" className="hidden" onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ""; if (f.length) upload(f); }} data-testid="dealer-photo-dropzone-input" />
          <span><ImagePlus size={28} className="mx-auto mb-2" />{t("photos.pick")}</span>
        </label>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {photos.map((p, i) => (
            <div key={p.id} className={`xd-panel relative ${i === 0 ? "ring-1 ring-[var(--xd-red)]" : ""}`} data-testid={`dealer-photo-${i}`}>
              {i === 0 && <span className="absolute top-1 start-1 z-10 bg-[var(--xd-red)] text-white text-[10px] px-1.5 py-0.5" data-testid="dealer-photo-cover">{t("photos.cover")}</span>}
              <img src={media(p.id, "thumb")} alt="" className="aspect-[4/3] w-full object-cover" />
              <div className="p-1.5 space-y-1">
                <span className={`block text-[11px] ${p.status === "approved" ? "text-emerald-400" : p.status === "rejected" ? "text-red-400" : "text-amber-300"}`}>{t(`photo.${p.status}`)}</span>
                <div className="flex flex-wrap items-center justify-between">
                  {canOrder && <button type="button" className={tile} disabled={i === 0} onClick={() => move(i, i - 1)} aria-label={t("photos.move_earlier")} data-testid={`dealer-photo-earlier-${i}`}><ChevronRight size={16} /></button>}
                  {canOrder && <button type="button" className={tile} disabled={i === photos.length - 1} onClick={() => move(i, i + 1)} aria-label={t("photos.move_later")} data-testid={`dealer-photo-later-${i}`}><ChevronLeft size={16} /></button>}
                  {canOrder && i > 0 && <button type="button" className={tile} onClick={() => move(i, 0)} aria-label={t("photos.make_cover")} title={t("photos.make_cover")} data-testid={`dealer-photo-make-cover-${i}`}><Star size={15} /></button>}
                  {!(v.published && p.status === "approved") && <button type="button" onClick={() => remove(p.id)} className={`${tile} hover:!text-red-400`} aria-label={t("common.delete")} data-testid={`dealer-photo-delete-${i}`}><Trash2 size={15} /></button>}
                </div>
              </div>
              {p.reject_reason && <p className="px-2 pb-2 text-xs text-red-400">{p.reject_reason}</p>}
            </div>
          ))}
          {previews.map((u) => <div key={u} className="xd-panel relative"><img src={u} alt="" className="aspect-[4/3] w-full object-cover opacity-50" /><span className="absolute inset-0 grid place-items-center text-xs">{t("photos.uploading")}</span></div>)}
        </div>
      )}
    </Section>
  );
};

const nOrNull = (x) => (x === "" || x == null ? null : Number(x));
const draftSpecs = (s) => Object.fromEntries(Object.entries(s).map(([k, x]) => [k, ["year", "mileage", "hand", "horsepower"].includes(k) ? nOrNull(x) : (x === "" ? null : x)]));
const DRAFTABLE = ["draft", "rejected", "changes_requested"];

export function DealerVehicleForm() {
  const { id } = useParams();
  const { t, tErr } = useLang();
  const navigate = useNavigate();
  const [v, setV] = useState(id ? null : {});
  const [specs, setSpecs] = useState(EMPTY_SPECS);
  const [conf, setConf] = useState(EMPTY_CONF);
  const [restr, setRestr] = useState(RESTR);
  const [rules, setRules] = useState(false);
  const [busy, setBusy] = useState(false);
  const [profile, setProfile] = useState(null);
  const [override, setOverride] = useState(false);
  const [priceOk, setPriceOk] = useState(false);
  const localId = useRef(null);
  useEffect(() => { api.get("/dealer/profile").then((r) => setProfile(r.data)).catch(() => {}); }, []);

  const hydrate = (d) => {
    localId.current = d.id;
    setV(d);
    setSpecs(pickSpecs({ ...d, ...(d.pending_changes || {}) }));
    const c = Object.fromEntries(Object.entries(d.conf || {}).map(([k, x]) => [k, x ?? ""]));
    setConf({ ...EMPTY_CONF, ...c, contact_name: c.contact_override ? c.contact_name : "", contact_phone: c.contact_override ? c.contact_phone : "" });
    setOverride(!!d.conf?.contact_override);
    setRestr(d.restrictions);
    setRules(!!d.photo_rules_confirmed);
    setPriceOk(false);
  };
  useEffect(() => {
    if (id && id !== localId.current) api.get(`/dealer/vehicles/${id}`).then((r) => hydrate(r.data)).catch(() => navigate("/dealer"));
    if (!id) { localId.current = null; setV({}); setSpecs(EMPTY_SPECS); setConf(EMPTY_CONF); setRestr(RESTR); setRules(false); setPriceOk(false); }
  }, [id, navigate]);
  const bChanged = String(conf.final_price) !== String(v?.conf?.final_price ?? "");
  const needConfirm = !v?.id || bChanged || !v?.conf?.price_confirmed_at;
  const draftMode = !v?.id || (!v.published && DRAFTABLE.includes(v.review_status));
  const draftBody = () => ({ specs: draftSpecs(specs), restrictions: restr, photo_rules_confirmed: rules,
    conf: { asking_price: nOrNull(conf.asking_price), final_price: nOrNull(conf.final_price), contact_name: override ? conf.contact_name || null : null, contact_phone: override ? conf.contact_phone || null : null } });
  const fullBody = () => ({ specs: specsPayload(specs), restrictions: restr, photo_rules_confirmed: rules, price_confirmed: priceOk,
    conf: { asking_price: Number(conf.asking_price), final_price: Number(conf.final_price), contact_name: override ? conf.contact_name : null, contact_phone: override ? conf.contact_phone : null } });
  const adopt = (d) => {
    localId.current = d.id;
    setV(d);
    if (!id) navigate(`/dealer/v/${d.id}`, { replace: true });
  };
  const ensureId = async () => {
    if (v?.id) return v.id;
    const { data } = await api.post("/dealer/vehicles/draft", draftBody());
    adopt(data);
    return data.id;
  };
  const saveDraft = async () => {
    if (conf.asking_price && conf.final_price && Number(conf.final_price) > Number(conf.asking_price)) return toast.error(t("err.final_above_asking"));
    setBusy(true);
    try {
      const { data } = v?.id ? await api.put(`/dealer/vehicles/${v.id}/draft`, draftBody()) : await api.post("/dealer/vehicles/draft", draftBody());
      if (v?.id) setV(data); else adopt(data);
      toast.success(t("dealer.draft_saved"));
    } catch (err) { toast.error(tErr(err)); } finally { setBusy(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (Number(conf.final_price) > Number(conf.asking_price)) return toast.error(t("err.final_above_asking"));
    if (needConfirm && !priceOk) return toast.error(t("err.price_confirmation_required"));
    if (!rules) return toast.error(t("err.photo_rules_required"));
    if (draftMode && !(v?.photos || []).length) return toast.error(t("err.photos_required"));
    setBusy(true);
    try {
      if (draftMode) {
        const vid = await ensureId();
        hydrate((await api.post(`/dealer/vehicles/${vid}/submit`, fullBody())).data);
        toast.success(t("dealer.submitted"));
        navigate("/dealer/pending");
      } else {
        hydrate((await api.put(`/dealer/vehicles/${v.id}`, fullBody())).data);
        toast.success(t("common.saved"));
      }
    } catch (err) { toast.error(tErr(err)); } finally { setBusy(false); }
  };
  if (!v) return <Loader />;
  const id_ = v.id;

  return (
    <form onSubmit={save} className="space-y-6" data-testid="dealer-vehicle-form">
      <PageTitle eyebrow={v.code || t("dealer.new")} title={id_ ? ([v.brand, v.model, v.year].filter(Boolean).join(" ") || t("dealer.untitled")) : t("dealer.new")}>
        {id_ && <div className="text-sm space-y-1 text-end"><ReviewTag s={v.review_status} /><p className="text-zinc-500">{t("dealer.public_price")}: <span className="ltr-num text-white">{fmtPrice(v.advertised_price)}</span></p></div>}
      </PageTitle>
      {v.rejection_reason && <p className="border border-orange-600/60 bg-orange-950/30 text-orange-200 text-sm p-4" data-testid="dealer-rejection-reason">{t("dealer.admin_feedback")}: {v.rejection_reason}</p>}
      {v.published && <p className="text-xs text-zinc-500">{t("dealer.edit_live_note")}</p>}
      <DealerPhotos v={v} ensureId={ensureId} onChange={(d) => { if (!v.id) adopt(d); else setV({ ...v, photos: d.photos }); }} />
      <Section title={t("dealer.specs")}><SpecsForm value={specs} onChange={setSpecs} /></Section>
      <DealerPricing conf={conf} setConf={setConf} publicPrice={v.advertised_price} bChanged={bChanged} isNew={needConfirm} priceOk={priceOk} setPriceOk={setPriceOk} />
      <DealerContact conf={conf} setConf={setConf} override={override} setOverride={setOverride} profile={profile} />
      <Section title={t("broker.restrictions")}><RestrictionToggles value={restr} onChange={setRestr} /></Section>
      <PhotoRules checked={rules} onChange={setRules} />
      <div className="flex flex-col sm:flex-row gap-3">
        {draftMode && <button type="button" onClick={saveDraft} disabled={busy} className="btn-ghost w-full sm:w-auto justify-center" data-testid="dealer-save-draft-button"><Save size={16} />{t("dealer.save_draft")}</button>}
        <button disabled={busy || Number(conf.final_price) > Number(conf.asking_price)} className="btn-xd w-full sm:w-auto justify-center" data-testid={draftMode ? "dealer-submit-review-button" : "dealer-save-button"}>
          {draftMode ? <><Send size={16} />{t(v.submitted_at ? "dealer.resubmit" : "dealer.submit_review")}</> : t("common.save")}
        </button>
      </div>
      {draftMode && <p className="text-xs text-zinc-500">{t("dealer.submit_hint")}</p>}
    </form>
  );
}


export function DealerProfile() {
  const { t, tErr } = useLang();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/dealer/profile").then((r) => setP(r.data)); }, []);
  if (!p) return <Loader />;
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { setP((await api.put("/dealer/profile", { contact_person: p.contact_person, contact_phone: p.contact_phone })).data); toast.success(t("common.saved")); } catch (err) { toast.error(tErr(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="space-y-6 max-w-2xl" data-testid="dealer-profile-page">
      <PageTitle eyebrow={t("dealer.portal")} title={t("profile.title")} />
      <Section>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <p><span className="text-zinc-500 block text-xs">{t("common.id")}</span><span className="ltr-num">{p.uid}</span></p>
          <p><span className="text-zinc-500 block text-xs">{t("form.business_name")}</span>{p.business_name}</p>
          <p><span className="text-zinc-500 block text-xs">{t("profile.account_phone")}</span><span className="ltr-num">{p.phone}</span></p>
        </div>
        <div className="xd-line" />
        <p className="text-xs text-zinc-500">{t("profile.hint")}</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t("profile.contact_person")}><Input required className="xd-input" value={p.contact_person || ""} onChange={(e) => setP({ ...p, contact_person: e.target.value })} data-testid="profile-contact-person-input" /></Field>
          <Field label={t("conf.contact_phone")}><Input required type="tel" dir="ltr" className="xd-input" value={p.contact_phone || ""} onChange={(e) => setP({ ...p, contact_phone: e.target.value })} data-testid="profile-contact-phone-input" /></Field>
        </div>
        <button disabled={busy} className="btn-xd" data-testid="profile-save-button"><UserCog size={16} />{t("common.save")}</button>
      </Section>
    </form>
  );
}

export const GuideBody = ({ prefix, sections, testid }) => {
  const { t } = useLang();
  return (
    <div className="space-y-4" data-testid={testid}>
      {sections.map(([key, n]) => (
        <Section key={key} title={t(`${prefix}.${key}.t`)} testid={`${testid}-${key}`}>
          <ul className="space-y-2.5 text-sm text-zinc-300 leading-relaxed list-disc ps-5">
            {Array.from({ length: n }, (_, i) => <li key={i}>{t(`${prefix}.${key}.${i + 1}`, { phone: OFFICIAL_PHONE })}</li>)}
          </ul>
        </Section>
      ))}
    </div>
  );
};

export const GuideAccept = ({ home, testid }) => {
  const { t, tErr } = useLang();
  const { user, reload } = useAuth();
  const navigate = useNavigate();
  const [ok, setOk] = useState(false);
  if (user.guide_version === user.guide_current) {
    return <p className="text-sm text-emerald-400 flex items-center gap-2" data-testid={`${testid}-accepted`}><CheckCircle2 size={15} />{t("guide.accepted_on")} <span className="ltr-num">{fmtDate(user.guide_accepted_at)} · v{user.guide_version}</span></p>;
  }
  const accept = async () => { try { await api.post("/auth/guide/accept"); await reload(); navigate(home, { replace: true }); } catch (e) { toast.error(tErr(e)); } };
  return (
    <div className="xd-panel p-5 space-y-4 border-[var(--xd-red)]/50" data-testid={`${testid}-accept-box`}>
      <label className="flex items-start gap-3 text-sm"><Checkbox checked={ok} onCheckedChange={(c) => setOk(!!c)} className="mt-0.5" data-testid={`${testid}-checkbox`} />{t("guide.confirm")}</label>
      <button className="btn-xd" disabled={!ok} onClick={accept} data-testid={`${testid}-accept-button`}>{t("guide.accept")}</button>
    </div>
  );
};

const DEALER_GUIDE = [["how", 3], ["ads", 3], ["accuracy", 3], ["prices", 4], ["platforms", 3], ["contact", 3]];

export function DealerGuide() {
  const { t } = useLang();
  const { user } = useAuth();
  return (
    <div className="space-y-6 max-w-3xl">
      <PageTitle eyebrow={t("dealer.portal")} title={t("dguide.title")} />
      <p className="text-sm text-zinc-400 flex items-start gap-2"><BookOpen size={16} className="mt-0.5 shrink-0" />{t("dguide.intro")}</p>
      {user.guide_version === user.guide_current && <AssistantSection role="dealer" />}
      <GuideBody prefix="dguide" sections={DEALER_GUIDE} testid="dealer-guide" />
      <GuideAccept home="/dealer" testid="dealer-guide" />
    </div>
  );
}
